// CreaTorneo.tsx — «Organizza un torneo»: per chi ha Silver o Gold. Il server crea il torneo e il link /t/<codice>;
// chi lo crea ha il primo posto e manda il link agli altri tre (anche senza account).
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CampoNome } from "../gioco/CampoNome.tsx";
import { creaTorneo, salvaTokenTorneo } from "../servizi/tornei.ts";
import { leggiPreferenza, salvaPreferenza } from "../servizi/preferenze.ts";
import { vai } from "../servizi/percorso.ts";
import { segnaSpiegazione, spiegazioneVista } from "../servizi/spiegazioni.ts";
import { useAccount } from "../servizi/account.ts";
import { PERCORSO_ACCESSO, PERCORSO_PIANI, PERCORSO_TORNEO, PREFERENZE } from "../costanti.ts";
import { codiceErroreTorneo } from "./Torneo.tsx";

export function CreaTorneo({ onAnnulla }: { onAnnulla: () => void }) {
  const { t } = useTranslation();
  const account = useAccount();
  const [nome, setNome] = useState(() => (account.tipo === "registrato" ? account.io.nome : leggiPreferenza(PREFERENZE.nome)) ?? "");
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const nomeAccount = account.tipo === "registrato" ? account.io.nome : null;
  useEffect(() => {
    if (nomeAccount) setNome((n) => n || nomeAccount);
  }, [nomeAccount]);

  // v039: una creazione sola (prima il clic su «Crea» segnava la spiegazione vista e la creazione «diretta» ne faceva
  // una seconda: due partite, e si finiva nell'ultima)
  const diretta = useRef(false);
  const crea = (n: string) => {
    diretta.current = true;
    salvaPreferenza(PREFERENZE.nome, n);
    segnaSpiegazione("torneo");
    setInvio(true);
    setErrore(null);
    creaTorneo(n)
      .then((r) => {
        salvaTokenTorneo(r.codice, r.token);
        vai(`${PERCORSO_TORNEO}${r.codice}`);
      })
      .catch((err: unknown) => {
        setErrore(codiceErroreTorneo(err));
        setInvio(false);
      });
  };
  // v038: spiegazione già vista e nome noto → si crea subito, senza finestra da riempire
  const pianoOk = account.tipo === "registrato" && account.io.piano !== "base";
  const subito = pianoOk && spiegazioneVista("torneo") && !!nome.trim() && !errore;
  useEffect(() => {
    if (!subito || diretta.current) return;
    diretta.current = true;
    crea(nome.trim());
  }, [subito]); // eslint-disable-line react-hooks/exhaustive-deps
  if (subito)
    return (
      <div className="crea-sfida" role="status" aria-busy="true">
        <p className="nota-centrata">{t("gioco.creazione")}</p>
      </div>
    );

  if (account.tipo !== "registrato" || account.io.piano === "base") {
    const ospite = account.tipo !== "registrato";
    return (
      <div className="crea-sfida">
        <p className="nota">{t("torneo.come_funziona")}</p>
        <p className="pannello-testo">{t(ospite ? "torneo.serve_account" : "torneo.serve_silver")}</p>
        <div className="azioni-riga">
          <button type="button" className="pulsante pulsante-secondario" onClick={onAnnulla}>
            {t("generale.annulla")}
          </button>
          <button type="button" className="pulsante pulsante-primario" disabled={account.tipo === "caricamento"} onClick={() => vai(ospite ? PERCORSO_ACCESSO : PERCORSO_PIANI)}>
            {t(ospite ? "account.accedi" : "piani.passa_silver")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="crea-sfida"
      onSubmit={(e) => {
        e.preventDefault();
        const n = nome.trim();
        if (n) crea(n);
      }}
    >
      <p className="nota">{t("torneo.come_funziona")}</p>
      <CampoNome valore={nome} onCambia={setNome} />
      {errore && (
        <p className="admin-errore" role="alert">
          {t(`torneo.errore_${errore}`)}
        </p>
      )}
      <div className="azioni-riga">
        <button type="button" className="pulsante pulsante-secondario" onClick={onAnnulla}>
          {t("generale.annulla")}
        </button>
        <button type="submit" className="pulsante pulsante-primario" disabled={invio}>
          {invio ? t("gioco.creazione") : t("torneo.crea")}
        </button>
      </div>
    </form>
  );
}
